/**
 * Configuración de ejecución (instantánea inmutable) y registro de eventos (doc. 04 §1 y §13,
 * ADR 0010). El esquema de eventos sirve también para eventos reales futuros (`fuente: real`).
 */

import type { Categoria, DefinicionDeProceso, Rol, TipoEstacion } from './proceso';
import type { Politicas } from './espacio';
import type { Segundos } from './tiempo';

export const VERSION_MOTOR = '0.4.0';

/** Réplicas automáticas (doc. 04 §11): primera tanda y máximo. */
export const REPLICAS_INICIALES = 20;
export const REPLICAS_MAXIMAS = 200;

export interface EstacionCompilada {
  /** Código visible: B01, R01, P01, L01, E01, ING, SAL. */
  id: string;
  tipo: TipoEstacion;
  /** `null` = sin límite. */
  capacidad: number | null;
  /** Tipos de unidad admitidos; `null` = todos. */
  compatibles: string[] | null;
  /** Estación especializada (por ejemplo, Fast Lane): se prefiere para los tipos que admite. */
  especializada: boolean;
  sigueTurno: boolean;
  x: number;
  z: number;
  rotacion: number;
  elementoId?: string;
}

export interface PersonaCompilada {
  /** Código visible: T01, A01. */
  id: string;
  rol: Rol;
}

export type TrasladosCompilados =
  | { modo: 'fijo'; duracionS: Segundos }
  | {
      modo: 'distancia';
      velocidadMs: number;
      maniobraS: Segundos;
      /** Distancia en metros por la ruta más corta: distancias[i][j], índices de `estaciones`. */
      distancias: (number | null)[][];
    };

export interface DemandaCompilada {
  modo: 'perfil' | 'agenda';
  unidadesPorDia: number;
  perfilHorario: number[];
  mezcla: Record<string, number>;
  inicioCitas: Segundos;
  ultimaCita: Segundos;
  agenda: { t: Segundos; tipoUnidad: string; atributos?: Record<string, boolean> }[];
}

export interface Huellas {
  layout: string;
  proceso: string;
  parametros: string;
  demanda: string;
  total: string;
}

export interface ConfiguracionDeEjecucion {
  versionMotor: string;
  /** Identificador determinista: huella total de la configuración. */
  ejecucionId: string;
  huellas: Huellas;
  proceso: DefinicionDeProceso;
  estaciones: EstacionCompilada[];
  personas: PersonaCompilada[];
  /** Turno diario de personas y estaciones que siguen turno. */
  turno: { inicio: Segundos; fin: Segundos };
  /** Hora del día en que se cierra cada jornada simulada (foto de pendientes). */
  cierre: Segundos;
  /** Horario de atención para promesas. */
  atencion: { inicio: Segundos; fin: Segundos };
  redondeoPromesaS: Segundos;
  dias: number;
  demanda: DemandaCompilada;
  traslados: TrasladosCompilados;
  politicas: Politicas;
  /**
   * Refrigerio de los técnicos (paso 1.2): hora de inicio (segundo del día), duración y si paran
   * todos a la vez o en dos grupos. Sin refrigerio, `null` u omitido.
   */
  refrigerio?: RefrigerioCompilado | null;
  semilla: number;
  /** Réplicas fijas o, con réplicas automáticas, las de la primera tanda. */
  replicas: number;
  /** Agregar réplicas hasta la precisión exigida (máximo `REPLICAS_MAXIMAS`). */
  replicasAutomaticas: boolean;
}

export interface RefrigerioCompilado {
  inicio: Segundos;
  duracionS: number;
  /** `escalonado`: T01, T03… paran a la hora de inicio y T02, T04… cuando vuelve el primer grupo. */
  modo: 'todos' | 'escalonado';
}

export type TipoEvento =
  | 'ejecucion.inicio'
  | 'ejecucion.fin'
  | 'unidad.creada'
  | 'unidad.llegada'
  | 'unidad.hito'
  | 'etapa.inicio'
  | 'etapa.fin'
  | 'etapa.interrupcion'
  | 'etapa.reanudacion'
  | 'espera.inicio'
  | 'espera.motivo'
  | 'espera.fin'
  | 'traslado.inicio'
  | 'traslado.fin'
  | 'recurso.reservado'
  | 'recurso.ocupado'
  | 'recurso.bloqueado'
  | 'recurso.liberado'
  | 'persona.turno-inicio'
  | 'persona.turno-fin'
  | 'persona.refrigerio-inicio'
  | 'persona.refrigerio-fin'
  | 'transicion.elegida'
  | 'unidad.salida'
  | 'unidad.pendiente-al-cierre'
  | 'incidencia';

export interface RecursoEnEvento {
  /** Tipo de estación o rol de la persona. */
  tipo: TipoEstacion | Rol;
  id: string;
}

export interface Evento {
  id: string;
  fuente: 'simulacion' | 'real';
  ejecucionId?: string;
  t: Segundos;
  dia: number;
  tipo: TipoEvento;
  unidadId?: string;
  etapaId?: string;
  /** En eventos de unidad que abren un tramo: categoría del tramo que empieza. */
  categoria?: Categoria;
  recursos?: RecursoEnEvento[];
  ubicacion?: { estacionId?: string };
  traslado?: {
    origen: string;
    destino: string;
    distanciaM: number | null;
    duracionS: Segundos;
    rutaId: string;
  };
  /** En esperas: tipos de recurso que faltan. */
  motivo?: string[];
  atributos?: Record<string, string | number | boolean | null>;
}

/** Datos de cada unidad creada (doc. 03 §3.5). */
export interface UnidadCreada {
  id: string;
  placa: string;
  tipoUnidad: string;
  cita: Segundos;
  atributos: Record<string, boolean>;
}

export interface PendienteAlCierre {
  dia: number;
  unidadId: string;
  etapaId: string;
  /** `en-cola` (con `bloqueando` si retiene su estación), `en-actividad`, `en-traslado`, `en-espera-externa`. */
  estado: EstadoUnidad;
  bloqueando: boolean;
  estacionId: string | null;
}

export type EstadoUnidad =
  'programada' | 'en-cola' | 'en-traslado' | 'en-actividad' | 'en-espera-externa' | 'finalizada';

export interface ErrorDeSimulacion {
  codigo:
    | 'invariante'
    | 'transicion-invalida'
    | 'liberacion-invalida'
    | 'bloqueo'
    | 'recurso-inexistente'
    | 'demasiados-eventos'
    | 'configuracion';
  /** Invariante violado (I1–I12), si corresponde. */
  invariante?: string;
  mensaje: string;
  t: Segundos;
  unidadId?: string;
}

export interface ResultadoReplica {
  replica: number;
  ok: boolean;
  error?: ErrorDeSimulacion;
  eventos: Evento[];
  unidades: UnidadCreada[];
  pendientes: PendienteAlCierre[];
  diagnostico: { invariantesRevisados: number; eventosProcesados: number };
}
